import { Inject, Injectable } from '@angular/core';
import { combineLatest, from, mergeMap, Observable, of, switchMap, throwError } from 'rxjs';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { UserGidService } from '../user-gid/user-gid.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map, take, tap } from 'rxjs/operators';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { ConcurrentStorageData } from '../../types/concurrent-usage';
import { GWRequestType, GWResponseType } from './concurrent-usage.type';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { safeStringify } from '@evorait/pace-components';
import { ConcurrentUsageDbService } from './concurrent-usage-db.service';
import { DEVICE_LOCK_KEY } from '../device/device.utils';

@Injectable({ providedIn: 'root' })
export class ConcurrentUsageService {
    private url: string;

    public constructor(
        private readonly http: HttpClient,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly userGidService: UserGidService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly paceInsights: PaceInsightsService,
        private readonly concurrentUsageDbService: ConcurrentUsageDbService,
    ) {
        // URL for posting single key value pair or GETTING values from key value store
        this.url = this.uriUtilities.getUriForContent() + ENDPOINTS.UserProfileSet;
    }

    public downloadAll(): Observable<unknown> {
        return this.http.get<GWResponseType>(this.url + `?$format=json`).pipe(
            map((res) => {
                this.log.debug(`ConcurrentUsageService: downloadAll: SUCCESS loading all data`);
                return res.d.results
                    .filter((entry) => !!entry?.value && entry.value.length > 0)
                    .map((entry) => JSON.parse(EncodingUtilities.base64DecodeUnicode(entry.value)));
            }),
            mergeMap(async (results: ConcurrentStorageData[]) => {
                await this.concurrentUsageDbService.clear();
                await this.concurrentUsageDbService.bulkAdd(
                    results.filter((result) => typeof result === 'object' && !!result.key && !!result.value),
                );
            }),
        );
    }

    public uploadAll(): Observable<unknown> {
        return combineLatest([this.uploadPresentEntries(), this.uploadDeletedEntries()]);
    }

    public writeDataForKey(key: string, value: string, timestamp: number): Observable<ConcurrentStorageData> {
        this.log.debug(
            `ConcurrentUsageService: writeDataForKey: writing data for key "${key}" at timestamp "${timestamp}"`,
        );
        const concurrencyData: ConcurrentStorageData = { key, value, timestamp };

        // store locally
        return from(this.concurrentUsageDbService.put(concurrencyData)).pipe(map(() => concurrencyData));
    }

    public readDataForKey(key: string): Observable<ConcurrentStorageData[]> {
        this.log.debug(`ConcurrentUsageService: readDataForKey: loading data for key "${key}"`);

        return from(this.concurrentUsageDbService.get(key)).pipe(
            take(1),
            map((data) => {
                if (!data?.value) {
                    return [];
                }
                return [data];
            }),
        );
    }

    public liveQueryDataForKey$(key: string): Observable<ConcurrentStorageData | undefined> {
        return this.concurrentUsageDbService.liveQuery$(key);
    }

    public deleteDataForKey(key: string): Observable<unknown> {
        this.log.debug(`ConcurrentUsageService: deleteDataForKey: deleting key "${key}"`);
        return from(this.concurrentUsageDbService.remove(key));
    }

    private uploadDeletedEntries(): Observable<unknown> {
        this.paceInsights.trackEvent('ConcurrentUsageService::uploadDeletedEntries');
        return from(this.concurrentUsageDbService.getDeletedEntries()).pipe(
            switchMap((allKeys: string[]) => {
                this.log.debug(
                    `ConcurrentUsageService: uploadDeletedEntries: uploading deletion of ${allKeys.join(', ')} (${allKeys.length}) entries to remote`,
                );

                if (allKeys.length === 0) {
                    return of(true);
                }

                return combineLatest(allKeys.map((key) => this.deleteRemote(key)));
            }),
        );
    }

    private uploadPresentEntries(): Observable<unknown> {
        this.paceInsights.trackEvent('ConcurrentUsageService::uploadPresentEntries');
        return from(this.concurrentUsageDbService.getPresentEntries()).pipe(
            map((allEntries) => allEntries.filter((entry) => entry.key !== DEVICE_LOCK_KEY)),
            switchMap((allEntries: ConcurrentStorageData[]) => {
                this.log.debug(
                    `ConcurrentUsageService: uploadAll: uploading ${allEntries.length} entries to remote: ${allEntries.map((entry) => entry.key).join(', ')}`,
                );

                if (allEntries.length === 0) {
                    return of(true);
                }

                return combineLatest(allEntries.map((entry) => this.writeRemote(entry)));
            }),
        );
    }

    /**
     * Reads concurrency data explicitly from remote store.
     *
     * Please note, this will not read from local storage, use `readDataForKey` for general usage.
     *
     * @param key The key to read.
     * @returns Observable<ConcurrentStorageData[]> An observable that emits the concurrency data read from remote store.
     */
    public readRemote(key: string): Observable<ConcurrentStorageData[]> {
        this.log.debug(`ConcurrentUsageService: readRemote: loading data for key "${key}"`);
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::readRemote::attempt', { id, key });

        return this.http
            .get<GWResponseType>(this.url + `?$filter=key eq '${this.generateKeyForUser(key)}'&$format=json`)
            .pipe(
                map((res) => {
                    this.log.debug(`ConcurrentUsageService: readDataForKey: SUCCESS loading data for key "${key}"`);
                    return res.d.results
                        .filter((entry) => entry.value && entry.value.length > 0)
                        .map((entry) => JSON.parse(EncodingUtilities.base64DecodeUnicode(entry.value)));
                }),
                tap((response) => {
                    this.paceInsights.trackEvent('ConcurrentUsageService::readDataForKey::success', {
                        id,
                        key,
                        response: safeStringify(response),
                    });
                }),
                catchError((error) => {
                    this.paceInsights.trackEvent('ConcurrentUsageService::readDataForKey::error', {
                        id,
                        key,
                        error: safeStringify(error),
                    });
                    const errorMessage = `ConcurrentUsageService: readDataForKey: ERROR data with following key could not be read from UserProfileSet: ${key}. Error ${safeStringify(error)}`;
                    this.log.error(errorMessage);
                    return throwError(() => new Error(errorMessage));
                }),
            );
    }

    /**
     * Writes concurrency data explicitly to remote store.
     *
     * Please note, this will not write into local storage, use `writeDataForKey` for general usage.
     *
     * @param concurrencyData The concurrency data to write.
     * @returns Observable<unknown> An observable that completes when the write operation is done.
     */
    public writeRemote(concurrencyData: ConcurrentStorageData): Observable<unknown> {
        const body: GWRequestType = {
            key: this.generateKeyForUser(concurrencyData.key),
            value: EncodingUtilities.base64EncodeUnicode(JSON.stringify(concurrencyData)),
            gid: this.userGidService.getUserGid(),
        };
        this.log.debug(
            `ConcurrentUsageService: uploadRemote: writing data for key "${concurrencyData.key}" at timestamp "${concurrencyData.timestamp}"`,
        );
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemote::attempt', { id, key: concurrencyData.key });
        return this.http.post<void>(this.url, body).pipe(
            tap(() => {
                this.log.debug(
                    `ConcurrentUsageService: uploadRemote: SUCCESS writing data for key "${concurrencyData.key}" at timestamp "${concurrencyData.timestamp}"`,
                );
                this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemote::success', {
                    id,
                    key: concurrencyData.key,
                });
            }),
            catchError((error) => {
                this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemote::error', {
                    id,
                    key: concurrencyData.key,
                    error: safeStringify(error),
                });
                const errorMessage = `ConcurrentUsageService: uploadRemote: ERROR data with following key could not be written to UserProfileSet: ${concurrencyData.key}. Error ${safeStringify(error)}`;
                this.log.error(errorMessage);
                return throwError(() => new Error(errorMessage));
            }),
        );
    }

    /**
     * Deletes concurrency data explicitly from remote store.
     *
     * Please note, this will not delete from local storage, use `deleteDataForKey` for general usage.
     *
     * @param key The key to delete.
     * @returns Observable<unknown> An observable that completes when the delete operation is done.
     */
    public deleteRemote(key: string): Observable<unknown> {
        // TODO: Gateway is lacking the delete feature
        // For now we are filtering the returned values for non-empty entries
        const body: GWRequestType = {
            key: this.generateKeyForUser(key),
            value: '',
            gid: this.userGidService.getUserGid(),
        };
        this.log.debug(`ConcurrentUsageService: deleteRemote: removing data for key "${key}"`);
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::deleteRemote::attempt', { id, key });

        return this.http.post<void>(this.url, body).pipe(
            tap(() => {
                this.log.debug(`ConcurrentUsageService: deleteRemote: SUCCESS removing data for key "${key}"`);
                this.paceInsights.trackEvent('ConcurrentUsageService::deleteRemote::success', {
                    id,
                    key,
                });
            }),
            catchError((error) => {
                this.paceInsights.trackEvent('ConcurrentUsageService::deleteRemote::error', {
                    id,
                    key,
                    error: safeStringify(error),
                });
                const errorMessage = `ConcurrentUsageService: deleteRemote: ERROR data with following key could not be deleted from UserProfileSet: ${key}. Error ${safeStringify(error)}`;
                this.log.error(errorMessage);
                return throwError(() => new Error(errorMessage));
            }),
        );
    }

    private generateKeyForUser(key: string): string {
        return `${this.userGidService.getUserGid()}-${key}`;
    }
}
